import React from "react";
import { CalendarClock, AlertTriangle } from "lucide-react";

/**
 * "When is the next action due" chip.
 *
 * Shared by the enquiry list and the detail header so the two can never drift on
 * what counts as overdue — a list saying "tomorrow" while the detail page says
 * "overdue" is the kind of thing that quietly destroys trust in the number.
 */

/** Relative wording, because "in 2 days" is read faster than a date. */
export function describeDue(dueAt) {
  if (!dueAt) return null;

  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;

  const now = new Date();
  const diffMs = due - now;
  const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((dayStart(due) - dayStart(now)) / 86_400_000);

  if (diffMs < 0) {
    // Overdue by less than a day still reads as "today" on a calendar, but the
    // counsellor needs to know it has already slipped.
    const hours = Math.floor(-diffMs / 3_600_000);
    if (hours < 1) return { tone: "overdue", text: "Overdue" };
    if (hours < 24) return { tone: "overdue", text: `Overdue by ${hours}h` };
    return { tone: "overdue", text: `Overdue by ${Math.floor(hours / 24)}d` };
  }

  if (dayDiff === 0) {
    const time = due.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return { tone: "today", text: `Today ${time}` };
  }
  if (dayDiff === 1) return { tone: "scheduled", text: "Tomorrow" };
  if (dayDiff < 7) return { tone: "scheduled", text: `In ${dayDiff} days` };

  return {
    tone: "scheduled",
    text: due.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
  };
}

export default function FollowUpDue({ dueAt, className = "" }) {
  const due = describeDue(dueAt);
  if (!due) return null;

  const Icon = due.tone === "overdue" ? AlertTriangle : CalendarClock;

  return (
    <span className={`fu-due fu-due--${due.tone} ${className}`} title={new Date(dueAt).toLocaleString("en-IN")}>
      <Icon size={11} /> {due.text}
    </span>
  );
}
